/**
 * Iter60.p44 — Attendance row project-split chip.
 *
 * When a daily attendance row carries `project_allocations` (multi-project
 * time split from the compact Split-by-projects dialog), the PROJECT
 * column on the Attendance grid must render a visible summary chip so
 * operators can SEE the split exists — otherwise the row looks
 * indistinguishable from a plain "no project" row and users have no
 * way to know their save landed.
 *
 * Behaviour:
 *   * N == 1  →  `[Project name] Hh Mm`
 *   * N >= 2  →  `N projects · <total> Hh Mm`   (bold N, muted total)
 *   * Hover tooltip shows the full per-project breakdown
 *     `Project A: 3h 0m` newline `Project B: 5h 48m`.
 *   * Clicking the chip forwards to the parent's `onOpen` (the same
 *     handler the row-menu's "Split by projects…" uses), so operators
 *     can iterate quickly.
 */
import React from "react";
import { Layers } from "lucide-react";
import { formatProjectLabel } from "./ProjectPicker";

function _fmtHM(minutes) {
    const m = Math.max(0, Math.round(Number(minutes) || 0));
    const h = Math.floor(m / 60);
    const rem = m % 60;
    if (h && rem) return `${h}h ${rem}m`;
    if (h) return `${h}h`;
    return `${rem}m`;
}

// Iter60.p49.1 — single-project chip prefers Job # (`code`). Falls
// back to `{code} · {name}` when there's room, or bare code, then
// name, then a hash slice. Matching helper for the tooltip.
function _projectDisplay(projectsById, alloc) {
    const p = (projectsById || {})[alloc.project_id];
    if (p) {
        const code = (p.code || "").trim();
        // Chip is tight — prefer just the code when we have one.
        return code || (p.name || "").trim() || alloc.project_id.slice(0, 6);
    }
    return alloc.project_name || alloc.project_id.slice(0, 6);
}

function _projectFullLabel(projectsById, alloc) {
    const p = (projectsById || {})[alloc.project_id];
    if (p) return formatProjectLabel(p) || alloc.project_id.slice(0, 6);
    return alloc.project_name || alloc.project_id.slice(0, 6);
}

export default function ProjectSplitChip({
    allocations,
    projectsById,
    onOpen,
    testid,
}) {
    const rows = (allocations || []).filter(
        (a) => a && a.project_id && Number(a.minutes) > 0,
    );
    if (rows.length === 0) return null;

    const totalMin = rows.reduce((s, a) => s + (Number(a.minutes) || 0), 0);
    const first = rows[0];
    // Iter60.p49.1 — chip prefers Job # (code); tooltip always shows
    // the full `{code} · {name}` for context.
    const firstName = _projectDisplay(projectsById, first);
    const isMulti = rows.length > 1;

    const tooltip = rows
        .map((a) => {
            const label = _projectFullLabel(projectsById, a);
            const note = a.note ? ` — ${a.note}` : "";
            return `${label}: ${_fmtHM(a.minutes)}${note}`;
        })
        .join("\n");

    return (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                onOpen && onOpen();
            }}
            title={tooltip}
            data-testid={testid}
            className="asl-project-split-chip inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] cursor-pointer hover:brightness-95"
            style={{
                background: "rgba(27, 42, 86, 0.06)",
                border: "1px solid rgba(27, 42, 86, 0.14)",
                color: "var(--asl-navy-2, #1B2A56)",
                fontWeight: 600,
                lineHeight: 1.1,
                maxWidth: 220,
            }}
        >
            <Layers className="h-3 w-3" aria-hidden="true" />
            {isMulti ? (
                <span className="whitespace-nowrap">
                    <span className="mono">{rows.length}</span>
                    {" projects · "}
                    <span
                        className="mono"
                        style={{ color: "var(--asl-text-3)" }}
                    >
                        {_fmtHM(totalMin)}
                    </span>
                </span>
            ) : (
                <span className="whitespace-nowrap truncate">
                    <span className="truncate">{firstName}</span>
                    {" · "}
                    <span
                        className="mono"
                        style={{ color: "var(--asl-text-3)" }}
                    >
                        {_fmtHM(first.minutes)}
                    </span>
                </span>
            )}
        </button>
    );
}
